import AddIcon from '@mui/icons-material/Add'
import { Button, createFilterOptions, FormControl, FormLabel } from '@mui/material'
import { createSelector } from '@reduxjs/toolkit'
import { get } from 'lodash'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useActions } from '../hooks/useActions.jsx'
import { pathToPerson, renderContent } from '../utils/helpers.js'
import AddNewModal from './AddNewModal.jsx'
import ConditionsList from './ConditionsList.jsx'
import IntegerDetails from './IntegerDetails.jsx'
import SearchSelect from './SearchSelect.jsx'
import TextFieldQuestion from './TextFieldQuestion.jsx'

const filter = createFilterOptions()

const DiagnosedAge = ({ diagnosedAge, setDiagnosedAge }) => (
  <FormControl>
    <FormLabel>Age of first diagnosis</FormLabel>
    <IntegerDetails value={diagnosedAge} onChange={setDiagnosedAge} />
  </FormControl>
)
const TreatingHospital = ({ hospital, setHospital }) => (
  <TextFieldQuestion
    label="Treating hospital"
    value={hospital}
    handleChange={setHospital}
    fullWidth
    multiline={false}
  />
)

const selectConditions = (id) =>
  createSelector(
    (state) => get(state, [...pathToPerson(id), 'conditions']),
    (list) => list.filter(({ type }) => type === 'phenotype'),
  )

export default function MedicalConditions({ id, label, config }) {
  const [modalOpened, setModalOpened] = useState(false)
  const [condition, setCondition] = useState({})
  const [diagnosedAge, setDiagnosedAge] = useState('')
  const [hospital, setHospital] = useState('')
  // convert vocabulary to { value, label } format for Autocomplete
  const hpoVocabulary = useSelector((state) =>
    get(state, ['hpoVocabulary', 'vocabularyTerms']),
  ).map((item) => ({ value: item.id, label: item.displayName }))
  const conditions = useSelector(selectConditions(id)) || []
  const { setPersonConditions, removePersonConditions } = useActions()

  return (
    <FormControl key="addMedicalCondition">
      <FormLabel sx={{ mb: 1 }}>{label}</FormLabel>
      <ConditionsList
        items={conditions}
        removeCondition={(item) => removePersonConditions({ id, conditionId: item.id })}
        editCondition={editCondition}
      />
      <Button
        sx={{ alignSelf: 'flex-start', mt: 2 }}
        startIcon={<AddIcon />}
        onClick={() => setModalOpened(true)}
      >
        Add Medical Condition
      </Button>
      <AddNewModal
        open={modalOpened}
        handleClose={handleClose}
        onSubmit={addDiagnosis}
        dialogTitle="Add Medical Condition"
        disabled={!condition?.value || !condition?.label}
        cancelButton
      >
        <SearchSelect
          key="searchMedicalConditions"
          label="Search Conditions"
          value={condition}
          options={hpoVocabulary}
          onChange={(_, newValue) => setCondition(newValue)}
          filterOptions={(options, params) => {
            const filtered = filter(options, params)
            const otherOption = {
              value: 'Other',
              label: 'Other',
            }

            // always keep other option in view
            if (!filtered.find((item) => item.value === otherOption.value)) {
              filtered.push(otherOption)
            }

            return filtered
          }}
        />
        {condition?.value === 'Other' && (
          <TextFieldQuestion
            key="otherCondition"
            label="Please describe the condition"
            value={condition.description || ''}
            handleChange={(e) => setCondition((prev) => ({ ...prev, description: e }))}
            fullWidth
            multiline={false}
          />
        )}
        {renderContent({
          renderMap: {
            relativesConditionsAge: (
              <DiagnosedAge key="relativesConditionsAge" {...{ diagnosedAge, setDiagnosedAge }} />
            ),
            patientMedicalConditionsAge: (
              <DiagnosedAge
                key="patientMedicalConditionsAge"
                {...{ diagnosedAge, setDiagnosedAge }}
              />
            ),
            patientMedicalConditionsHospital: (
              <TreatingHospital
                key="patientMedicalConditionsHospital"
                {...{ hospital, setHospital }}
              />
            ),
            relativesConditionsHospital: (
              <TreatingHospital key="relativesConditionsHospital" {...{ hospital, setHospital }} />
            ),
          },
          config,
        })}
      </AddNewModal>
    </FormControl>
  )

  function editCondition(item) {
    const diagnosedAge = item.qualifiers.find(({ label }) => label === 'Age at diagnosis')?.type
    setCondition({
      value: item.id,
      label: item.label,
    })
    setDiagnosedAge(diagnosedAge ? parseInt(diagnosedAge) : '')
    setHospital(item.treatments.find(({ type }) => type === 'other')?.treatmentLocation || '')
    setModalOpened(true)
  }

  function addDiagnosis() {
    if (condition.value && condition.label) {
      const isOther = condition.value === 'Other'

      if (isOther && !condition.description) {
        handleClose()

        return
      }

      const conditionId = isOther ? condition.description : condition.value
      const conditionLabel = isOther ? condition.description : condition.label

      setPersonConditions({
        id,
        conditionId: condition.value,
        condition: {
          id: conditionId,
          label: conditionLabel,
          type: 'phenotype',
          qualifiers: diagnosedAge
            ? [
                {
                  label: 'Age at diagnosis',
                  type: diagnosedAge,
                },
              ]
            : [],
          treatments: hospital
            ? [
                {
                  type: 'other',
                  treatmentLocation: hospital,
                  isPerformed: 'Y',
                },
              ]
            : [],
        },
      })
    }
    handleClose()
  }

  function handleClose() {
    setCondition({})
    setDiagnosedAge('')
    setHospital('')
    setModalOpened(false)
  }
}
